import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Loader2, ArrowRight, Check, TrendingDown, TrendingUp, BarChart3 } from "lucide-react";

const fmt = (n) => `$${n.toLocaleString()}`;

const Compare = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["col"],
    queryFn: async () => (await api.get("/cost-of-living")).data,
  });

  const [selected, setSelected] = useState(["italy", "portugal", "spain"]);

  const toggle = (slug) => {
    setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));
  };

  const countries = data?.countries || [];
  const categories = data?.categories || [];
  const selectedCountries = useMemo(
    () => countries.filter((c) => selected.includes(c.slug)),
    [countries, selected]
  );

  // find lowest/highest per category among selected
  const extremes = useMemo(() => {
    const out = {};
    categories.forEach((cat) => {
      const vals = selectedCountries.map((c) => c.values[cat.key]);
      if (vals.length) {
        out[cat.key] = { min: Math.min(...vals), max: Math.max(...vals) };
      }
    });
    return out;
  }, [categories, selectedCountries]);

  const maxTotal = useMemo(
    () => Math.max(...(selectedCountries.map((c) => c.values.monthly_total) || [1]), 1),
    [selectedCountries]
  );

  if (isLoading) return <div className="min-h-screen flex justify-center items-center bg-[#F9F9F7]"><Loader2 className="w-8 h-8 animate-spin text-[#9A3412]" /></div>;

  return (
    <div className="bg-[#F9F9F7] min-h-screen">
      <Seo
        title="Cost of Living Comparison — 9 Countries Side by Side | UMoooV"
        description="Compare the monthly cost of living across Italy, France, Spain, Germany, Sweden, the UAE, Portugal, the USA and Canada — rent, utilities, groceries, transit and total budget, side by side. Plan your move abroad with real numbers."
        path="/compare"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
        <div className="mb-10 max-w-2xl">
          <p className="text-sm uppercase tracking-widest text-[#9A3412] mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" />Cost of living</p>
          <h1 className="font-serif text-5xl md:text-6xl font-medium text-stone-900">Compare destinations</h1>
          <p className="mt-4 text-stone-600">Estimated monthly costs for a single person in a mid-range city, in USD. Select countries to compare them side by side.</p>
        </div>

        {/* Country selector */}
        <div className="flex flex-wrap gap-2 mb-10">
          {countries.map((c) => {
            const on = selected.includes(c.slug);
            return (
              <button
                key={c.slug}
                data-testid={`compare-chip-${c.slug}`}
                onClick={() => toggle(c.slug)}
                className={`flex items-center gap-2 px-4 py-2 border text-sm transition-colors ${
                  on ? "border-[#9A3412] bg-[#9A3412] text-white" : "border-stone-300 bg-white text-stone-700 hover:border-stone-500"
                }`}
              >
                <span>{c.flag}</span>{c.country}{on && <Check className="w-3.5 h-3.5" />}
              </button>
            );
          })}
        </div>

        {selectedCountries.length === 0 ? (
          <div className="border border-dashed border-stone-300 bg-white py-20 text-center text-stone-500">Select at least one country above to start comparing.</div>
        ) : (
          <>
            {/* Comparison table */}
            <div className="overflow-x-auto border border-stone-200 bg-white mb-12">
              <table className="w-full min-w-[640px]" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left p-5 text-sm uppercase tracking-widest text-stone-500 font-medium">Monthly cost</th>
                    {selectedCountries.map((c) => (
                      <th key={c.slug} className="p-5 text-center">
                        <div className="text-3xl">{c.flag}</div>
                        <div className="font-serif text-xl text-stone-900 mt-1">{c.country}</div>
                        <div className="text-xs text-stone-400">{c.local_currency}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat, ri) => (
                    <tr key={cat.key} className={`border-b border-stone-100 ${cat.key === "monthly_total" ? "bg-stone-50" : ""}`}>
                      <td className={`p-5 text-stone-700 ${cat.key === "monthly_total" ? "font-semibold" : ""}`}>{cat.label}</td>
                      {selectedCountries.map((c) => {
                        const v = c.values[cat.key];
                        const isMin = selectedCountries.length > 1 && v === extremes[cat.key]?.min;
                        const isMax = selectedCountries.length > 1 && v === extremes[cat.key]?.max;
                        return (
                          <td key={c.slug} data-testid={`cell-${cat.key}-${c.slug}`} className={`p-5 text-center tabular-nums ${cat.key === "monthly_total" ? "font-serif text-2xl text-stone-900" : "text-stone-700"}`}>
                            <span className="inline-flex items-center gap-1">
                              {fmt(v)}
                              {isMin && <TrendingDown className="w-4 h-4 text-[#166534]" title="Lowest" />}
                              {isMax && !isMin && <TrendingUp className="w-4 h-4 text-[#B45309]" title="Highest" />}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total bars */}
            <div className="border border-stone-200 bg-white p-8 mb-12">
              <h2 className="font-serif text-2xl text-stone-900 mb-6">Estimated monthly total</h2>
              <div className="space-y-4">
                {[...selectedCountries].sort((a, b) => a.values.monthly_total - b.values.monthly_total).map((c) => (
                  <div key={c.slug} className="flex items-center gap-4">
                    <div className="w-32 shrink-0 text-sm text-stone-700 flex items-center gap-2"><span>{c.flag}</span>{c.country}</div>
                    <div className="flex-1 bg-stone-100 h-8 relative overflow-hidden">
                      <div className="h-full bg-[#9A3412] flex items-center justify-end px-3 transition-all duration-700" style={{ width: `${(c.values.monthly_total / maxTotal) * 100}%` }}>
                        <span className="text-white text-sm font-medium tabular-nums">{fmt(c.values.monthly_total)}</span>
                      </div>
                    </div>
                    <Link to={`/guides/${c.slug}`} data-testid={`compare-guide-link-${c.slug}`} className="text-sm text-[#9A3412] font-medium flex items-center gap-1 hover:gap-2 transition-all shrink-0">Guide <ArrowRight className="w-3.5 h-3.5" /></Link>
                  </div>
                ))}
              </div>
              <p className="text-xs text-stone-400 mt-6">Figures are approximate planning estimates for a single person in a mid-range city, in USD. Actual costs vary by city, lifestyle, and exchange rates. See each guide's full cost-of-living breakdown for detail.</p>
            </div>

            {/* CTA */}
            <div className="bg-stone-900 text-white p-10 text-center">
              <h2 className="font-serif text-3xl md:text-4xl font-medium">Ready to go deeper?</h2>
              <p className="text-stone-300 mt-3 max-w-xl mx-auto">Each guide includes a full cost-of-living breakdown plus visas, housing, healthcare, taxes, and a first-90-days checklist. Grab the bundle for all 9.</p>
              <Button asChild className="mt-6 bg-white text-stone-900 hover:bg-stone-200 rounded-none px-8 py-6"><Link to="/guides/all-access-bundle" data-testid="compare-bundle-cta">Get all 9 guides · $29.99 <ArrowRight className="w-4 h-4 ml-2" /></Link></Button>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Compare;
